// VideoPlayer — Phase 5 Task 2.
//
// Scope for this task (no audio preview sync — that lives in Task 5.5):
//   - Render a <video> element when this asset has a loaded blob or an admin
//     videoPath, with metadata-deferred duration write-back into useVideoStore.
//   - Clean up on asset switch: remove any src/srcObject, load(), revoke
//     prior blob URLs via useVideoStore.clear().
//   - Dashed-border upload zone (drop / file-select → Blob → store) when the
//     asset has video enabled but no source yet.
//   - Collapsable video-reference-section (caret header strip). The timeline
//     always stays visible — the header only toggles the video frame.
//   - Custom assets: always mount the Video Reference panel (no toggle). The
//     panel starts collapsed; opening it reveals the upload zone or loaded
//     video. Predefined assets keep their `allowVideo` gate.
//
// Not in scope yet: play/pause sync with the timeline preview, ducking,
// mixer popover, export buttons, minimap, recording focus overlay.
//
// Legacy reference: index.html:2213–2249 (markup) + initVideoPlayer
// (function at line 5975) for the source-selection + cleanup flow we port
// into React effects here.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type RefObject,
} from 'react';
import type { Asset } from '@shared/types';
import { useSessionStore } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import { ensureMediaSession, streamVideoUrl } from '@/lib/api';
import { releaseActiveVideo, setActiveVideo } from '@/lib/activeVideo';
import { invalidatePreviewAudio } from '@/lib/audioContext';
import { getTimelinePlaybackController } from '@/lib/timelinePlayback';
import { validateVideoFile } from '@/lib/videoValidation';
import { scrollParentOf } from '@/lib/scrollParent';
import styles from './VideoPlayer.module.css';

/** Blob-size threshold above which we force-recreate the preview
 *  AudioContext on the next play. Safari under memory pressure can flip
 *  the context into a silent-running state where ac.state === 'running'
 *  but no audio reaches the speakers; invalidating recovers it. */
const LARGE_BLOB_THRESHOLD = 200 * 1024 * 1024;

function formatDur(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec - m * 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** WebDAV stream retry strategy (see the comment inside the component). */
const MAX_VIDEO_RETRIES = 3;
const retryDelaysMs = [500, 1500, 3000] as const;

interface VideoPlayerProps {
  /** Sticky Timeline-Band (TTSView). Ist das Video zu mehr als der Hälfte
   *  nach oben weggescrollt, dockt das <video> rechts in dieses Band — gleiche
   *  Höhe wie das Band, Breite aus dem Seitenverhältnis (Deckel
   *  `--video-dock-max-w`). Ohne Ref (STS, kein sticky Band) kein Docking. */
  dockBandRef?: RefObject<HTMLElement | null>;
}

/** Docken ab > 50 % verdeckt, lösen unter 40 % — die Hysterese verhindert
 *  Flackern, wenn das Talent genau an der Schwelle steht.
 *
 *  Zwei Befestigungen, damit nichts pro Frame nachgeführt werden muss (die
 *  erste Fassung war immer `position:fixed` und hing beim Scrollen einen
 *  Frame hinterher — „es wackelt", Philip 2026-10-03):
 *   • Band scrollt noch mit → Video `position:absolute` im Scroll-Container
 *     (AppShell .contentScroll ist position:relative) in Inhaltskoordinaten;
 *     es scrollt nativ zusammen mit dem Band.
 *   • Band haftet (Oberkante = Pane-Oberkante) → `position:fixed`; die
 *     Position steht dann fest.
 *  An der Übergangsstelle fallen beide Positionen zusammen, kein Sprung. */
const DOCK_IN = 0.5;
const DOCK_OUT = 0.4;
/** Toleranz in px, ab wann das Band als „haftet" gilt. */
const STUCK_EPS = 1.5;
/** Innenabstand des Bandes (TTSView .timelineSticky: padding 6px 0). */
const BAND_PAD = 6;
/** Abstand zwischen gedocktem Video und Timeline. */
const DOCK_GAP = 12;
const DOCK_MAX_W = 320;


export function VideoPlayer({ dockBandRef }: VideoPlayerProps = {}) {
  const toolId = useSessionStore((s) => s.toolId);
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const configAssets = useSessionStore((s) => s.config?.assets);
  const customAssetsRaw = useSessionStore((s) => s.customAssets);

  const videoBlobs = useVideoStore((s) => s.blobs);
  const videoSources = useVideoStore((s) => s.sources);
  const videoDurations = useVideoStore((s) => s.durations);
  const setBlob = useVideoStore((s) => s.setBlob);
  const setSource = useVideoStore((s) => s.setSource);
  const setDuration = useVideoStore((s) => s.setDuration);
  const clearVideo = useVideoStore((s) => s.clear);
  const setCustomDuration = useSessionStore((s) => s.setCustomDuration);

  // Resolve the active asset. We mirror `selectAllAssets` here (instead of
  // subscribing to it directly) because the session store's factory rebuilds
  // the array on every call — subscribing would cause infinite re-renders.
  const activeAsset: Asset | undefined = useMemo(() => {
    const cfg = configAssets ?? [];
    const custom = customAssetsRaw.map((ca) => ({
      ...ca,
      assetType: 'free' as const,
      _isCustom: true as const,
    }));
    return [...cfg, ...custom][currentAssetIdx];
  }, [configAssets, customAssetsRaw, currentAssetIdx]);

  // Whether the Video Reference panel mounts for this asset. Predefined
  // assets honour the admin `allowVideo` flag. Custom assets always mount
  // the panel — no per-asset opt-in toggle (the panel just starts collapsed,
  // and opening it reveals the upload zone).
  const videoEnabled = useMemo(() => {
    if (!activeAsset) return false;
    if (activeAsset._isCustom) return true;
    return !!activeAsset.allowVideo;
  }, [activeAsset]);

  // Source resolution (legacy initVideoPlayer, lines 6064–6077).
  // Priority: user-uploaded blob URL > admin videoPath stream. If neither
  // applies but video is enabled, we render the upload zone.
  const hasBlob = !!videoBlobs[currentAssetIdx];
  const hasAdminPath = !!activeAsset?.videoPath;
  const resolvedSrc: string | null = useMemo(() => {
    if (!videoEnabled) return null;
    if (hasBlob) return videoSources[currentAssetIdx] ?? null;
    if (hasAdminPath && toolId && activeAsset?.videoPath) {
      return streamVideoUrl(toolId, activeAsset.videoPath);
    }
    return null;
  }, [
    videoEnabled,
    hasBlob,
    hasAdminPath,
    videoSources,
    currentAssetIdx,
    toolId,
    activeAsset,
  ]);

  // Multi-Lingual (Spec 2026-09-21 § 6): Videos kommen dort ausschließlich
  // vom Admin — das Talent lädt keine eigenen hoch, ersetzt und entfernt
  // auch keine. Der Player bleibt reine Referenz.
  const multiLingual = useSessionStore((s) => s.config?._simpleMode === true);
  const allowOwnVideo = !multiLingual;

  const showUploadZone = videoEnabled && !resolvedSrc && allowOwnVideo;

  // Collapse state — toggled via the header caret. Always expanded when there's
  // no video section to hide (i.e. audio-only) so the user can't collapse an
  // empty strip. Predefined assets stay expanded by default to mirror the
  // legacy behaviour where `allowVideo` implies intent. Custom assets start
  // collapsed only while there is nothing to show (no admin opt-in, so we
  // don't assume the user wants the empty upload zone open); once a video
  // exists the panel opens by default — the blob is keyed by asset index and
  // survives the TTS↔STS switch, which remounts this component, and the
  // talent shouldn't have to hunt for a video they just loaded (GitHub #2).
  const isCustom = !!activeAsset?._isCustom;
  const hasVideoSource = hasBlob || hasAdminPath;
  const [collapsed, setCollapsed] = useState(isCustom && !hasVideoSource);
  useEffect(() => {
    // Reset on asset switch (and on the TTS↔STS remount). Reading
    // `hasVideoSource` here without listing it as a dep is intentional:
    // a later change of the source must not re-collapse the panel.
    setCollapsed(isCustom && !hasVideoSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAssetIdx, isCustom]);
  useEffect(() => {
    // Auto-open once a source appears after mount — the IndexedDB restore
    // hands the blob over asynchronously. Never auto-collapses: after
    // "Remove" the upload zone should stay visible.
    if (hasVideoSource) setCollapsed(false);
  }, [hasVideoSource]);
  const canCollapse = videoEnabled;

  // Video element wiring: clear src on asset switch, set it to resolvedSrc
  // once we have one, and write duration back into the store on metadata load.
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Tracks whether the <video> has rendered its first frame yet. Used to
  // gate a spinner overlay while a WebDAV stream is still buffering — local
  // blob sources decode instantly so the overlay never shows for them.
  const [firstFrameReady, setFirstFrameReady] = useState(false);


  // ── WebDAV stream auto-retry ─────────────────────────────────────────────
  // The admin `videoPath` branch streams the reference video through the
  // Server → WebDAV proxy. Occasionally — particularly on asset switch —
  // the <video> element fires `error` before it ever paints a frame
  // (transient network blip, range-request hiccup, intermediate proxy
  // dropping the connection). The user's tolerance for a fresh load is
  // high (we already show a spinner), so silently re-fetching with a
  // cache-busting query param recovers the most common failures without
  // making them notice.
  //
  // Strategy: up to 3 retries with exponential backoff (500ms, 1500ms,
  // 3000ms). Each retry re-assigns `src` with `?retry=N` appended so the
  // browser bypasses any partially-cached / partially-failed response.
  // Only kicks in when there's no user blob (i.e. the WebDAV branch);
  // user-uploaded blob URLs use `URL.createObjectURL` and have nothing to
  // re-fetch.
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);
  const [retrying, setRetrying] = useState(false);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current != null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  // A WebDAV stream is in play when there's no local blob but we still
  // resolved a src (the admin `videoPath` branch in `resolvedSrc`). We use
  // this to scope the spinner overlay — local files load instantly so we
  // don't want to flash a spinner for them.
  const isWebDavStream = !!resolvedSrc && !hasBlob;

  // Register the active <video> with the module-level singleton in
  // src/lib/activeVideo.ts so hooks like useVideoDucking can reach it
  // imperatively without needing to thread the ref through the tree.
  //
  // Teardown only ever releases the element THIS instance registered
  // (`releaseActiveVideo`), never a blind null: on the custom-asset TTS↔STS
  // switch the replacement VideoPlayer attaches its <video> in the layout
  // phase, and our passive unmount cleanup runs after that — an
  // unconditional `setActiveVideo(null)` there left the new view without a
  // video for its timeline preview (2026-09-15). `ownedElRef` remembers the
  // element past the ref-detach so the cleanup can still name it.
  const ownedElRef = useRef<HTMLVideoElement | null>(null);
  const registerVideoRef = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    if (el) {
      ownedElRef.current = el;
      setActiveVideo(el);
    } else {
      releaseActiveVideo(ownedElRef.current);
    }
  }, []);
  useEffect(() => {
    return () => {
      releaseActiveVideo(ownedElRef.current);
    };
  }, []);

  // Single source-of-truth effect for the <video> element's src. Keyed on
  // BOTH `resolvedSrc` and `currentAssetIdx` so a navigation back to an
  // already-resolved URL (which would yield an identical resolvedSrc string
  // and thus skip a [resolvedSrc]-only effect) still forces a fresh
  // resource-selection cycle. WebDAV streams in particular won't repaint
  // the first frame unless we re-call `.load()` after re-assigning src.
  //
  // We also can't trust a `v.src === resolvedSrc` short-circuit: the IDL
  // getter returns a normalized absolute URL while `resolvedSrc` is the
  // relative `/api/...` form, so they're never strictly equal — and the
  // previous two-effect split (clear-on-idx, set-on-src) caused a race
  // where switching to a different WebDAV asset left the element with the
  // src set but no completed resource-selection, hence a black frame.
  const lastWiredRef = useRef<{ src: string | null; idx: number }>({
    src: null,
    idx: -1,
  });
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const prev = lastWiredRef.current;
    // Bail on unrelated re-renders (e.g. videoSources got a new reference
    // because some other asset's blob upload mutated the record) where our
    // own slot is unaffected.
    //
    // BUT: also re-wire whenever the <video> element is in the DOM with no
    // src yet. This recovers from the navigation pattern:
    //   Asset A (video) → wires src, lastWiredRef = (A, srcA)
    //   Asset B (no video, videoEnabled=false) → component returns null,
    //     <video> unmounts, ref → null. Effect runs with !v and bails
    //     WITHOUT touching lastWiredRef.
    //   Asset A again → component re-mounts, ref → fresh element with no
    //     .src. Effect runs; (resolvedSrc, currentAssetIdx) still equal the
    //     stored prev, so without the v.src check we'd bail and leave the
    //     element source-less → black frame, no error event, no spinner.
    const needsFreshSrc = !!resolvedSrc && !v.src;
    if (
      !needsFreshSrc &&
      prev.src === resolvedSrc &&
      prev.idx === currentAssetIdx
    ) return;
    lastWiredRef.current = { src: resolvedSrc, idx: currentAssetIdx };

    try { v.pause(); } catch { /* ignore */ }
    // Any src change — including clearing — invalidates the previously
    // decoded first frame, so hide the spinner-gate until the new source
    // (if any) reports `loadeddata`.
    setFirstFrameReady(false);
    // A legitimate src/asset change resets the retry budget — any pending
    // retry timer for the previous source must be cancelled so it can't
    // clobber the new src after the user has navigated away.
    clearRetryTimer();
    retryCountRef.current = 0;
    setRetrying(false);

    if (!resolvedSrc) {
      v.removeAttribute('src');
      try { v.load(); } catch { /* ignore */ }
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    // Force a fresh resource-selection cycle — assigning `src` then
    // calling `.load()` is what makes the browser refetch a WebDAV stream
    // when the URL changes between assets.
    v.src = resolvedSrc;
    try { v.currentTime = 0; } catch { /* ignore */ }
    try { v.load(); } catch { /* ignore */ }
  }, [resolvedSrc, currentAssetIdx, clearRetryTimer]);

  // Cancel any in-flight retry timer when the component unmounts.
  useEffect(() => clearRetryTimer, [clearRetryTimer]);

  // loadedmetadata → write duration; error → show a friendly message.
  const onLoadedMetadata = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const d = v.duration;
    setLoading(false);
    // A retry that produced metadata is a successful retry — drop the
    // retry-in-progress flag so the spinner overlay's "retry attempt N/3"
    // hint disappears once the frame is on screen.
    setRetrying(false);
    retryCountRef.current = 0;
    if (Number.isFinite(d) && d > 0) {
      setDuration(currentAssetIdx, d);
      // Auto-set the custom-asset target duration (ceil to 0.1s) when the
      // user loads a free-length asset's media for the first time. Skip if:
      //   - asset isn't custom (predefined assets respect admin config)
      //   - admin already set a `targetDuration`
      //   - user already entered a custom duration manually
      if (activeAsset?._isCustom && !activeAsset.targetDuration) {
        const current =
          useSessionStore.getState().customDurations[currentAssetIdx];
        if (!current) {
          setCustomDuration(currentAssetIdx, Math.ceil(d * 10) / 10);
        }
      }
    }
  }, [activeAsset, currentAssetIdx, setCustomDuration, setDuration]);

  // <video> error handler with auto-retry for the WebDAV stream branch.
  // On user-uploaded blobs we can't recover by re-fetching, so we fall
  // straight through to the error banner. On a streamed admin video we
  // schedule up to MAX_VIDEO_RETRIES re-loads with cache-busting query
  // params before giving up.
  const onVideoError = useCallback(() => {
    setLoading(false);
    setFirstFrameReady(false);

    // Blob path or no resolvable src — nothing to retry.
    if (hasBlob || !resolvedSrc || !isWebDavStream) {
      setError('Could not load this video reference.');
      return;
    }

    if (retryCountRef.current >= MAX_VIDEO_RETRIES) {
      setRetrying(false);
      setError(
        'Could not load this video reference. Please reload the page to try again.',
      );
      return;
    }

    const attempt = retryCountRef.current + 1;
    const delay =
      retryDelaysMs[Math.min(attempt - 1, retryDelaysMs.length - 1)];
    retryCountRef.current = attempt;
    setRetrying(true);
    setError(null);

    clearRetryTimer();
    retryTimerRef.current = window.setTimeout(async () => {
      retryTimerRef.current = null;
      // Eine abgelaufene Media-Session (12 h) sieht für <video> aus wie jeder
      // andere Ladefehler — vor dem Retry erneuern.
      if (toolId) await ensureMediaSession(toolId);
      const v = videoRef.current;
      if (!v) return;
      // Cache-bust so the browser bypasses any partially-failed cached
      // response. The `?retry=N` param doesn't survive into the WebDAV
      // call (the server proxies the path, not the query) so it's safe
      // to append without breaking the upstream fetch.
      const sep = resolvedSrc.includes('?') ? '&' : '?';
      const bustedSrc = `${resolvedSrc}${sep}retry=${attempt}`;
      setLoading(true);
      try { v.pause(); } catch { /* ignore */ }
      v.src = bustedSrc;
      try { v.currentTime = 0; } catch { /* ignore */ }
      try { v.load(); } catch { /* ignore */ }
    }, delay);
  }, [hasBlob, isWebDavStream, resolvedSrc, clearRetryTimer, toolId]);

  // First frame decoded — drop the spinner overlay. `loadeddata` is the
  // earliest event that guarantees `readyState >= HAVE_CURRENT_DATA`, i.e.
  // the browser has at least one renderable frame.
  const onLoadedData = useCallback(() => {
    setFirstFrameReady(true);
    // Successful first-frame paint clears any retry-in-progress flag so
    // the overlay text reverts to the plain spinner.
    setRetrying(false);
    retryCountRef.current = 0;
  }, []);

  // Click on the video routes through the unified timeline-preview pipeline
  // (`useTimelinePreview` owned by Timeline.tsx) instead of standalone
  // HTML5 playback. The Timeline registers its play/stop on a module-level
  // singleton (src/lib/timelinePlayback.ts); we read it here so the video
  // click triggers the same flow as the timeline's play button — auto-
  // ducking, SFX bed, playhead movement, and Web Audio mixing.
  //
  // The start path always uses `playFromStart` (seek(0) → play) instead of
  // resuming from the current playhead. Resume-from-paused-position used to
  // emit a leftover audio snippet from wherever the user last stopped —
  // visually confusing when the user just clicked the video frame expecting
  // a fresh preview from the start. Back-to-0 + play matches that
  // expectation.
  //
  // Fallback: if no Timeline is mounted (defensive — there's exactly one
  // Timeline per view), we toggle native playback so the user isn't stuck
  // staring at a frozen video.
  const onVideoClick = useCallback(() => {
    const ctl = getTimelinePlaybackController();
    if (ctl) {
      if (ctl.isPlaying()) {
        ctl.stop();
      } else {
        ctl.playFromStart();
      }
      return;
    }
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {
        /* autoplay blocked — ignore */
      });
    } else {
      v.pause();
    }
  }, []);

  // ── Upload flow ────────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);

  const ingestFile = useCallback(
    async (file: File) => {
      // Validate at the input boundary — codec/container/size/duration. This
      // catches issues that would otherwise blow up later in MP4Box demux,
      // <video> preview, or MP4 export. See src/lib/videoValidation.ts.
      const result = await validateVideoFile(file);
      if (!result.ok) {
        setError(result.error);
        return;
      }
      for (const warn of result.warnings) {
        console.warn('[VideoPlayer] video validation warning:', warn);
      }
      // Clear previous blob/source first so `setSource` revokes the old URL.
      clearVideo(currentAssetIdx);
      const url = URL.createObjectURL(file);
      setBlob(currentAssetIdx, file);
      setSource(currentAssetIdx, url);
      setError(null);
      // Large local blobs can silently break the preview AudioContext on
      // Safari (ctx.state stays 'running' but emits no audio). Force a
      // rebuild on the next play. Legacy loadVideoElement L6193.
      if (file.size > LARGE_BLOB_THRESHOLD) {
        invalidatePreviewAudio(
          `large video loaded (${Math.round(file.size / 1024 / 1024)} MB)`,
        );
      }
    },
    [clearVideo, currentAssetIdx, setBlob, setSource],
  );

  const onUploadZoneClick = () => fileInputRef.current?.click();
  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void ingestFile(f);
    // Reset so the same file can be re-selected.
    e.target.value = '';
  };

  // ── Replace / Remove (local blob only) ─────────────────────────────────
  const onReplaceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    replaceInputRef.current?.click();
  };
  const onReplaceInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void ingestFile(f);
    e.target.value = '';
  };
  const onRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Remove the loaded video?')) return;
    clearVideo(currentAssetIdx);
    setError(null);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) void ingestFile(f);
  };
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = () => setDragOver(false);

  // ── Docking ins sticky Timeline-Band (2026-10-03) ─────────────────────
  // Philip: Bild-in-Bild rechts neben der Timeline, fix auf die Höhe des
  // sticky Bereichs, kein Platzverbrauch solange das Video im Bild ist. Das
  // <video> bleibt dasselbe Element (Timeline-Preview, Ducking und
  // activeVideo-Singleton hängen daran) — es wechselt nur auf position:fixed
  // an die gemessene Stelle; `.body` behält als Platzhalter seine Höhe, damit
  // der Scroll nicht springt. Das Band bekommt `--video-dock-w` als
  // padding-right, damit Timeline und Export-Leiste Platz machen.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const dockedRef = useRef(false);
  const [dock, setDock] = useState<{ mode: 'fixed' | 'absolute'; style: CSSProperties; placeholderH: number } | null>(null);
  const dockActive = !!dockBandRef && !!resolvedSrc && !collapsed;
  useEffect(() => {
    const band = dockBandRef?.current;
    if (!dockActive || !band) {
      dockedRef.current = false;
      setDock(null);
      return;
    }
    let raf = 0;
    const measure = () => {
      raf = 0;
      const body = bodyRef.current;
      const v = videoRef.current;
      if (!body || !band.isConnected) return;
      const bodyRect = body.getBoundingClientRect();
      const bandRect = band.getBoundingClientRect();
      if (bodyRect.height <= 0) return;
      // Wie viel vom Video ist oben aus dem Scroll-Bereich gescrollt? Die
      // Band-Oberkante taugt dafür nicht: Solange das Band noch nicht haftet,
      // liegt es UNTER dem Video (Abstand > Videohöhe) — das las sich wie
      // „komplett verdeckt" und dockte schon bei Scroll 0 (Sichtprüfung
      // 2026-10-03). Haftet das Band, ist seine Oberkante ohnehin die
      // Pane-Oberkante.
      const scroller = scrollParentOf(band);
      const sRect = scroller?.getBoundingClientRect();
      const paneTop = sRect?.top ?? 0;
      const hidden = Math.max(0, Math.min(1, (paneTop - bodyRect.top) / bodyRect.height));
      const wasDocked = dockedRef.current;
      const nowDocked = wasDocked ? hidden > DOCK_OUT : hidden > DOCK_IN;
      const stuck = !scroller || bandRect.top <= paneTop + STUCK_EPS;
      dockedRef.current = nowDocked;
      if (!nowDocked) {
        band.style.removeProperty('--video-dock-w');
        band.removeAttribute('data-video-docked');
        setDock((d) => (d ? null : d));
        return;
      }
      const aspect = v && v.videoWidth > 0 && v.videoHeight > 0 ? v.videoWidth / v.videoHeight : 16 / 9;
      const height = Math.max(0, bandRect.height - 2 * BAND_PAD);
      const maxW = parseFloat(getComputedStyle(band).getPropertyValue('--video-dock-max-w')) || DOCK_MAX_W;
      const width = Math.min(maxW, Math.round(height * aspect));
      band.style.setProperty('--video-dock-w', `${width + DOCK_GAP}px`);
      band.setAttribute('data-video-docked', '1');
      const mode: 'fixed' | 'absolute' = stuck ? 'fixed' : 'absolute';
      const style: CSSProperties = mode === 'fixed'
        ? {
            top: Math.round(bandRect.top + BAND_PAD),
            right: Math.max(0, Math.round(window.innerWidth - bandRect.right)),
            height: Math.round(height),
            width,
          }
        : {
            // Inhaltskoordinaten des Scroll-Containers (sRect ist hier gesetzt).
            top: Math.round(bandRect.top - sRect!.top + scroller!.scrollTop + BAND_PAD),
            left: Math.round(bandRect.right - sRect!.left + scroller!.scrollLeft - width),
            height: Math.round(height),
            width,
          };
      // Nur bei echter Änderung neu rendern — der Scroll-Handler läuft pro
      // Frame, die Werte stehen in beiden Befestigungen aber fest.
      setDock((d) => {
        if (d && d.mode === mode && d.style.top === style.top && d.style.right === style.right
          && d.style.left === style.left && d.style.height === style.height && d.style.width === style.width) return d;
        return { mode, style, placeholderH: d?.placeholderH ?? bodyRect.height };
      });
    };
    const schedule = () => { if (!raf) raf = window.requestAnimationFrame(measure); };
    measure();
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    const ro = new ResizeObserver(schedule);
    ro.observe(band);
    return () => {
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
      band.style.removeProperty('--video-dock-w');
      band.removeAttribute('data-video-docked');
      dockedRef.current = false;
    };
  }, [dockActive, dockBandRef, currentAssetIdx]);

  // Don't render anything when the asset is not loaded, or when video is
  // disabled (predefined asset without admin `allowVideo`). Timeline +
  // AssetLengthControl live outside this component and handle the audio-only
  // case independently. Custom assets always pass the `videoEnabled` check
  // above, so they always mount the panel.
  if (!activeAsset) return null;
  if (!videoEnabled) return null;

  const duration = videoDurations[currentAssetIdx] ?? 0;

  const rootCls = [
    styles.root,
    resolvedSrc ? styles.hasVideo : '',
    collapsed ? styles.collapsed : '',
    canCollapse ? '' : styles.noCollapsable,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootCls} data-sticky-push="header">
      <div
        className={styles.header}
        onClick={() => {
          if (canCollapse) setCollapsed((c) => !c);
        }}
      >
        <i className={`ph-bold ph-caret-down ${styles.caret}`} />
        <i className={`ph-bold ph-film-strip ${styles.headerIcon}`} />
        <span className={styles.title}>Video Reference</span>
        {duration > 0 && (
          <span className={styles.meta}>{formatDur(duration)}</span>
        )}
        {hasBlob && allowOwnVideo && (
          <div
            className={styles.headerActions}
            // Swallow ALL clicks bubbling out of the actions area so the
            // header's collapse-toggle never fires from inside. Critically:
            // the hidden file `<input>`'s programmatic .click() (triggered
            // by Replace) emits a synthetic click event that bubbles up
            // through this container; stopPropagation on the buttons alone
            // doesn't catch the input's event. Guarding at the wrapper
            // level covers buttons + input + any future children.
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className={styles.headerActionBtn}
              onClick={onReplaceClick}
              title="Replace the loaded video"
            >
              <i className="ph-bold ph-arrow-clockwise" />
              <span>Replace</span>
            </button>
            <button
              type="button"
              className={styles.headerActionBtn}
              onClick={onRemoveClick}
              title="Remove the loaded video"
            >
              <i className="ph-bold ph-trash" />
              <span>Remove</span>
            </button>
            <input
              ref={replaceInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
              style={{ display: 'none' }}
              onChange={onReplaceInputChange}
            />
          </div>
        )}
      </div>

      <div
        ref={bodyRef}
        className={styles.body}
        style={dock ? { minHeight: dock.placeholderH } : undefined}
      >
        {resolvedSrc && (
          <div
            ref={wrapRef}
            className={[
              styles.elementWrap,
              loading ? styles.loading : '',
              dock ? styles.docked : '',
              dock?.mode === 'absolute' ? styles.dockedAbsolute : '',
            ].filter(Boolean).join(' ')}
            style={dock?.style}
          >
            <video
              ref={registerVideoRef}
              preload="metadata"
              playsInline
              onLoadedMetadata={onLoadedMetadata}
              onLoadedData={onLoadedData}
              onError={onVideoError}
              onClick={onVideoClick}
            />
            {isWebDavStream && !firstFrameReady && (
              <div className={styles.spinnerOverlay} aria-hidden="true">
                <i className={`ph-bold ph-circle-notch ${styles.spinner}`} />
                {retrying && (
                  <div className={styles.spinnerHint}>
                    Reloading video… ({retryCountRef.current}/{MAX_VIDEO_RETRIES})
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {error && (
          <div className={styles.errorBanner} role="alert">
            <i className="ph-bold ph-warning-circle" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        {showUploadZone && (
          <div
            className={[styles.uploadZone, dragOver ? styles.dragover : '']
              .filter(Boolean)
              .join(' ')}
            onClick={onUploadZoneClick}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <div className={styles.uploadIcon}>
              <i className="ph-bold ph-film-strip" />
            </div>
            <div className={styles.uploadText}>Add a reference video</div>
            <div className={styles.uploadHint}>
              Drag &amp; drop MP4, WebM, or MOV — or click to browse
            </div>
            <div className={styles.uploadHintSmall}>
              Video stays in your browser — not uploaded unless you choose to
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
              style={{ display: 'none' }}
              onChange={onFileInputChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}
